import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import type { Movie } from "@/mocks/movies";

interface DetailHeroProps {
  movie: Movie;
  onShare: () => void;
}

export default function DetailHero({ movie, onShare }: DetailHeroProps) {
  return (
    <div className="relative w-full bg-primary-950 overflow-hidden">
      <div className="absolute inset-0 opacity-25">
        <img
          src={movie.banner ?? movie.cover}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/95 to-primary-950/60"></div>

      <div className="relative w-full max-w-page mx-auto px-4 md:px-8 py-7 md:py-10">
        <Breadcrumb
          dark
          items={[
            { label: "首页", to: "/" },
            { label: movie.type, to: `/category/${movie.type}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-6 flex flex-col sm:flex-row gap-6 md:gap-8">
          <div className="w-[150px] sm:w-[190px] md:w-[230px] shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-primary-700/70 bg-primary-900">
              <img
                src={movie.cover}
                alt={`${movie.title}封面`}
                title={`${movie.title} - 影视详情与看片应用推荐`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-3 rounded-md bg-primary-900/80 border border-primary-800 px-3 py-2 text-center">
              <p className="text-[11px] text-primary-300">站内评分</p>
              <p className="mt-0.5 text-xl font-bold text-accent-400 font-label flex items-center justify-center gap-1">
                <i className="ri-star-fill text-base"></i>
                {movie.rating.toFixed(1)}
              </p>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-[11px] font-medium">
                {movie.type}
              </span>
              <span className="px-2 py-0.5 rounded border border-primary-700 text-primary-200 text-[11px]">
                {movie.year}
              </span>
              <span className="px-2 py-0.5 rounded border border-primary-700 text-primary-200 text-[11px]">
                {movie.region}
              </span>
              <span className="text-[11px] text-primary-300 flex items-center gap-1">
                <i className="ri-fire-line text-accent-400"></i>
                {movie.heat}
              </span>
            </div>

            <h1 className="mt-3 text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              {movie.title}
            </h1>
            <p className="mt-2 text-sm text-accent-300">{movie.tagline}</p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-xs md:text-sm">
              <p className="flex gap-2 text-primary-300">
                <span className="text-primary-400 shrink-0">导演</span>
                <span className="text-background-100">{movie.director}</span>
              </p>
              <p className="flex gap-2 text-primary-300">
                <span className="text-primary-400 shrink-0">片长</span>
                <span className="text-background-100">{movie.duration}</span>
              </p>
              <p className="flex gap-2 text-primary-300">
                <span className="text-primary-400 shrink-0">主演</span>
                <span className="text-background-100">{movie.cast.join(" / ")}</span>
              </p>
              <p className="flex gap-2 text-primary-300">
                <span className="text-primary-400 shrink-0">类型</span>
                <span className="text-background-100">{movie.genres.join(" / ")}</span>
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <a
                href="#watch-apps"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-download-2-line"></i>
                获取看片APP
              </a>
              <button
                type="button"
                onClick={onShare}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-primary-600 text-primary-100 text-sm whitespace-nowrap hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>
                分享链接
              </button>
              <Link
                to={`/category/${movie.type}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-primary-800 text-primary-300 text-sm whitespace-nowrap hover:text-background-50 transition-colors"
              >
                更多{movie.type}
              </Link>
            </div>

            <p className="mt-3 text-[11px] text-primary-400">
              本站内容为影视资料展示，播放与下载功能为前端演示状态，不提供实际片源。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}